import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { gestorVisivel } from '../lib/filtroGestor'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer
} from 'recharts'

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
const ANO_ATUAL = new Date().getFullYear()

export default function AnalyticsAderencia() {
  const { isAdmin, isGestor, profile, isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const [chartData, setChartData] = useState([])
  const [loading, setLoading] = useState(true)
  const [anoInicio, setAnoInicio] = useState(ANO_ATUAL)
  const [anoFim, setAnoFim] = useState(ANO_ATUAL)
  const [filtroIndicador, setFiltroIndicador] = useState('')
  const [gestores, setGestores] = useState([])
  const [indicadores, setIndicadores] = useState([])

  // Filtro de gestor compartilhado com as outras telas (ver AuthContext).
  // Quem nao enxerga o seletor tambem nao herda o recorte.
  const podeFiltrarGestor = isAdmin || isSuperAdmin || isGestor
  const gestorSel = podeFiltrarGestor ? gestorVisivel(filtroGestor, gestores) : ''

  useEffect(() => {
    if (isAdmin || isGestor || isSuperAdmin) {
      let q = supabase.from('profiles').select('id, nome').order('nome')
      if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)
      q.then(({ data }) => setGestores(data || []))
    }
  }, [isAdmin, isGestor, isSuperAdmin, selectedCompany])

  useEffect(() => {
    // So' estrategicos: selecionar um tatico aqui produziria grafico vazio,
    // ja' que o calculo de aderencia os exclui.
    let q = supabase.from('indicadores').select('id, nome, gestor_id').order('nome')
      .eq('apenas_tatico', false)
    if (gestorSel) q = q.eq('gestor_id', gestorSel)
    if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)
    q.then(({ data }) => {
      setIndicadores(data || [])
      setFiltroIndicador('')
    })
  }, [gestorSel, selectedCompany])

  useEffect(() => { fetchData() }, [anoInicio, anoFim, gestorSel, filtroIndicador, selectedCompany])

  async function fetchData() {
    setLoading(true)

    // Aderencia considera apenas indicadores estrategicos (ver migration v1.5).
    let indsQuery = supabase.from('indicadores').select('id, objetivo, gestor_id')
      .eq('apenas_tatico', false)
    if (gestorSel) indsQuery = indsQuery.eq('gestor_id', gestorSel)
    if (filtroIndicador) indsQuery = indsQuery.eq('id', filtroIndicador)
    if (isSuperAdmin && selectedCompany) indsQuery = indsQuery.eq('company_id', selectedCompany.id)

    const [{ data: inds }, { data: res }] = await Promise.all([
      indsQuery,
      supabase.from('resultados').select('indicador_id, ano, mes, meta, resultado')
        .gte('ano', anoInicio).lte('ano', anoFim)
    ])

    if (!inds || !res) { setLoading(false); return }

    const indIds = new Set(inds.map(i => i.id))
    const indMap = Object.fromEntries(inds.map(i => [i.id, i]))

    const pontos = []
    for (let ano = anoInicio; ano <= anoFim; ano++) {
      for (let mes = 1; mes <= 12; mes++) {
        if (ano === ANO_ATUAL && mes > new Date().getMonth() + 1) break

        const resMes = res.filter(r =>
          r.ano === ano && r.mes === mes &&
          indIds.has(r.indicador_id) &&
          r.resultado != null && r.meta != null
        )

        if (resMes.length === 0) {
          pontos.push({ label: `${MESES[mes - 1]}/${String(ano).slice(2)}`, aderencia: null, ok: 0, total: 0 })
          continue
        }

        const ok = resMes.filter(r => {
          const ind = indMap[r.indicador_id]
          return ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta
        }).length

        pontos.push({
          label: `${MESES[mes - 1]}/${String(ano).slice(2)}`,
          aderencia: Math.round((ok / resMes.length) * 100),
          ok,
          total: resMes.length,
        })
      }
    }

    setChartData(pontos)
    setLoading(false)
  }

  const pontosComDados = chartData.filter(p => p.aderencia !== null)
  const mediaGeral = pontosComDados.length > 0
    ? Math.round(pontosComDados.reduce((s, p) => s + p.aderencia, 0) / pontosComDados.length)
    : null

  const melhor = pontosComDados.length > 0
    ? pontosComDados.reduce((a, b) => a.aderencia > b.aderencia ? a : b)
    : null
  const pior = pontosComDados.length > 0
    ? pontosComDados.reduce((a, b) => a.aderencia < b.aderencia ? a : b)
    : null

  const anos = []
  for (let a = ANO_ATUAL - 3; a <= ANO_ATUAL; a++) anos.push(a)

  return (
    <div>
      <div className="page-title">
        <div>
          <h2>Histórico de Aderência</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
            Evolução do percentual de metas atingidas ao longo do tempo
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {podeFiltrarGestor && gestores.length > 1 && (
            <select value={gestorSel} onChange={e => setFiltroGestor(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
              <option value="">Todos</option>
              {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          )}
          <select value={filtroIndicador} onChange={e => setFiltroIndicador(e.target.value)} style={{ width: 'auto', minWidth: 200 }}>
            <option value="">Todos os indicadores</option>
            {indicadores.map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
          </select>
          <select value={anoInicio} onChange={e => setAnoInicio(Number(e.target.value))} style={{ width: 'auto' }}>
            {anos.map(a => <option key={a} value={a}>De {a}</option>)}
          </select>
          <select value={anoFim} onChange={e => setAnoFim(Number(e.target.value))} style={{ width: 'auto' }}>
            {anos.filter(a => a >= anoInicio).map(a => <option key={a} value={a}>Até {a}</option>)}
          </select>
        </div>
      </div>

      {/* Resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Média do período', val: mediaGeral !== null ? `${mediaGeral}%` : '—', color: mediaGeral === null ? 'var(--text2)' : mediaGeral >= 80 ? 'var(--green)' : mediaGeral >= 50 ? 'var(--amber)' : 'var(--red)' },
          { label: `Melhor mês${melhor ? ` — ${melhor.label}` : ''}`, val: melhor ? `${melhor.aderencia}%` : '—', color: 'var(--green)' },
          { label: `Pior mês${pior ? ` — ${pior.label}` : ''}`, val: pior ? `${pior.aderencia}%` : '—', color: 'var(--red)' },
        ].map(m => (
          <div key={m.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '16px 20px', boxShadow: 'var(--shadow)'
          }}>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: m.color }}>{loading ? '—' : m.val}</div>
          </div>
        ))}
      </div>

      {/* Gráfico */}
      <div className="card">
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : pontosComDados.length === 0 ? (
          <div className="empty-state">Sem dados no período selecionado.</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="gradAderencia" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--blue)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--blue)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e0d8" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b6960' }} />
              <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: '#6b6960' }} />
              <Tooltip
                contentStyle={{ border: '1px solid #e2e0d8', borderRadius: 8, fontSize: 13 }}
                formatter={(v, _) => [v !== null ? `${v}%` : '—', 'Aderência']}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  const d = payload[0]?.payload
                  return (
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      {d.aderencia !== null ? (
                        <>
                          <div style={{ color: 'var(--blue)', fontWeight: 700, fontSize: 18 }}>{d.aderencia}%</div>
                          <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 2 }}>{d.ok} de {d.total} metas atingidas</div>
                        </>
                      ) : (
                        <div style={{ color: 'var(--text3)' }}>Sem dados</div>
                      )}
                    </div>
                  )
                }}
              />
              {mediaGeral !== null && (
                <ReferenceLine y={mediaGeral} stroke="#EF9F27" strokeDasharray="4 3" strokeWidth={1.5}
                  label={{ value: `Média ${mediaGeral}%`, position: 'right', fontSize: 11, fill: '#EF9F27' }} />
              )}
              <Area
                type="monotone" dataKey="aderencia" name="Aderência"
                stroke="var(--blue)" strokeWidth={2}
                fill="url(#gradAderencia)"
                dot={{ r: 3, fill: 'var(--blue)', strokeWidth: 0 }}
                connectNulls={false}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
